// Memory and state document constants shared by the api, the context builder and the web (docs/adr/OPEN-16, OPEN-17).
import type { MemoryKind, MemoryScope, StateDocKind } from './domain.js';

/** A state document describes current state, not history. About 1,700 tokens at 4.6 characters per token. */
export const STATE_DOC_MAX_CHARS = 8_000;

export const STATE_DOC_LABELS: Record<StateDocKind, string> = {
  production: 'Production state',
  dev: 'Development state',
  backlog: 'Backlog',
  feedback: 'Feedback digest',
};

export const MEMORY_ITEM_MAX_CHARS = 500;
/** Caps on what one conversation snapshots into its prefix. Newest items win when a cap is hit. */
export const PROJECT_MEMORY_SNAPSHOT_MAX_CHARS = 6_000;
export const USER_MEMORY_SNAPSHOT_MAX_CHARS = 1_500;

/** The headings the extraction prompt must use, in order, and the item each bullet under them becomes. */
export const MEMORY_HEADINGS = ['Constraints', 'Decisions', 'Open questions', 'Facts', 'About the person'] as const;
export type MemoryHeading = (typeof MEMORY_HEADINGS)[number];

export const MEMORY_HEADING_TARGET: Record<MemoryHeading, { scope: MemoryScope; kind: MemoryKind }> = {
  Constraints: { scope: 'project', kind: 'constraint' },
  Decisions: { scope: 'project', kind: 'decision' },
  'Open questions': { scope: 'project', kind: 'open_question' },
  Facts: { scope: 'project', kind: 'fact' },
  'About the person': { scope: 'user', kind: 'preference' },
};

export const MEMORY_KIND_LABELS: Record<MemoryKind, string> = {
  constraint: 'Constraints',
  decision: 'Decisions',
  open_question: 'Open questions',
  fact: 'Facts',
  preference: 'About the person',
};

/** Two memory texts are the same item when they match after this. Mirrors the checkpoint merge rule. */
export function normalizeMemoryText(text: string): string {
  return text.normalize('NFKC').replace(/\*\*|__/g, '').replace(/\s+/g, ' ').trim().toLowerCase().replace(/[.!;:]+$/, '');
}
